import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  Alert,
  AlertIcon,
  AlertDialogFooter,
  Button,
} from "@chakra-ui/react";
import { RoleType, Team } from "common";
import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAPI } from "../api/useAPI";
import { useAPIQuery } from "../api/useAPIQuery";
import { useOnAPISuccess } from "../api/useOnAPISuccess";
import { useToastOnAPIError } from "../api/useToastOnAPIError";
import { LOGGED_IN_HOME_PAGE } from "../config";
import { useIsInRole } from "../utils/useIsInRole";

interface TeamDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  team: Team;
}

export const TeamDeleteModal = ({
  isOpen,
  onClose,
  team,
}: TeamDeleteModalProps) => {
  const cancelRef = useRef(null);
  const isAdmin = useIsInRole(RoleType.Admin);

  const api = useAPI();
  const { data, state, error, query } = useAPIQuery<"teamDelete">({
    apiCall: api.teamDelete,
  });
  const navigate = useNavigate();
  useToastOnAPIError({ title: "Unable to delete team", error });
  useOnAPISuccess<"teamDelete">({
    data,
    state,
    onSuccess: () => {
      onClose();
      navigate(LOGGED_IN_HOME_PAGE);
    },
  });

  const onDelete = useCallback(() => {
    query({ id: team.id });
  }, [query, team.id]);

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Delete Team "{team.name}"
          </AlertDialogHeader>

          <AlertDialogBody>
            Are you sure? You can't undo this action afterwards.
            {!isAdmin ? (
              <Alert status="error" variant="left-accent" marginTop={2}>
                <AlertIcon />
                You must be an admin to delete youth kids
              </Alert>
            ) : null}
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose}>
              Cancel
            </Button>
            <Button
              isDisabled={!isAdmin}
              colorScheme="red"
              isLoading={state === "loading"}
              onClick={onDelete}
              ml={3}
            >
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};
